/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import CopyToClipboard from "react-copy-to-clipboard";
import { Link } from "react-router-dom";
import { Cell, TableCommonProps } from "react-table";
import styled from "styled-components";
import {
  Button,
  IndeterminateCheckbox,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import { SUCCESS_TEXT } from "~/constants";
import { showSimpleInfo } from "~/services/toast";
import { getProviderIconName, trimTextBeginning } from "~/tools";

type CloudResource = {
  id: string;
  provider: Provider;
  name: string;
  providerType?: string;
  drifted?: boolean;
  environment: {
    name: string;
    id: string;
    application: {
      name: string;
      id: string;
    };
  };
  pendingAssignment: {
    name: string;
    id: string;
    application: {
      name: string;
      id: string;
    };
  };
  action: (ids: string[], accept: boolean) => Promise<void>;
};

const Assignment = styled.div`
  span {
    display: block;
    font-size: 0.75rem;
  }
`;

export const DriftListColumns = [
  {
    id: "selection",
    Header: ({
      getToggleAllRowsSelectedProps,
    }: {
      getToggleAllRowsSelectedProps: () => TableCommonProps;
    }) => <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />,
    Cell: ({ row }: Cell<CloudResource>) => (
      <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
    ),
    width: "3%",
  },
  {
    accessor: "provider",
    width: "3%",
    disableSortBy: true,
    Cell: ({ row }: Cell<CloudResource>) => {
      return (
        <SVGIcon name={getProviderIconName(row.original.provider)} size="lg" />
      );
    },
  },
  {
    Header: "Type",
    accessor: "providerType",
    disableSortBy: true,
    width: "5rem",
    Cell: ({ row }: Cell<CloudResource>) => {
      return (
        <Text size="sm" tag="div" style={{ wordBreak: "break-all" }}>
          {row.original.providerType}
        </Text>
      );
    },
  },

  {
    Header: "Resource Id",
    accessor: "name",
    disableSortBy: true,
    Cell: ({ row }: Cell<CloudResource>) => {
      return (
        <div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Link to={`/estate/${row.original.id}`}>{row.original.id}</Link>
            <CopyToClipboard
              text={row.original.id}
              onCopy={() => {
                showSimpleInfo(SUCCESS_TEXT.copied);
              }}
            >
              <SVGIcon
                name="copy"
                size="sm"
                css={{ cursor: "pointer", marginLeft: theme.spacing.spacing02 }}
              />
            </CopyToClipboard>
          </div>
          <div>
            <Text size="sm" tag="span" color={theme.color.text.text02}>
              {trimTextBeginning(row.original.name, 30)}
            </Text>
            <CopyToClipboard
              text={row.original.name}
              onCopy={() => {
                showSimpleInfo(SUCCESS_TEXT.copied);
              }}
            >
              <SVGIcon
                name="copy"
                size="sm"
                css={{ cursor: "pointer", marginLeft: theme.spacing.spacing02 }}
              />
            </CopyToClipboard>
          </div>
        </div>
      );
    },
  },
  {
    Header: "Current",
    accessor: "account",
    disableSortBy: true,
    width: "15rem",
    Cell: ({ row }: Cell<CloudResource>) => (
      <Assignment>
        <span>{row?.original?.environment?.application?.name}</span>
        {row?.original?.environment?.name}
      </Assignment>
    ),
  },
  {
    Header: "",
    accessor: "empty",
    Cell: () => <SVGIcon name="arrowRight" size="sm" />,
    width: "3%",
    disableSortBy: true,
  },
  {
    Header: "Target",
    accessor: "target",
    width: "15rem",
    disableSortBy: true,
    Cell: ({ row }: Cell<CloudResource>) => (
      <Assignment>
        <span>{row?.original?.pendingAssignment?.application?.name}</span>

        {row?.original?.pendingAssignment?.name}
      </Assignment>
    ),
  },
  {
    Header: "Actions",
    accessor: "application",
    disableSortBy: true,
    width: "7rem",
    Cell: ({ row }: Cell<CloudResource>) => {
      return (
        <div style={{ display: "flex" }}>
          <Button
            size="md"
            severity="low"
            marginRight="0.5rem"
            onClick={() => row.original.action([row.original?.id], false)}
          >
            Reject
          </Button>{" "}
          <Button
            size="md"
            severity="low"
            onClick={() => row.original.action([row.original?.id], true)}
          >
            Approve
          </Button>
        </div>
      );
    },
  },
];
